/**
 * Claude Code for Kids — shared program constants.
 *
 * SINGLE SOURCE OF TRUTH for the sibling ladder, consumed by BOTH sides:
 *   - server/routes.ts (authoritative per-child line-item pricing)
 *   - client checkout  (display-only mirror; the server always recomputes)
 *
 * The percentages (35/50/65% off campers 2/3/4) are MARKETING COPY. The
 * product is these flat pre-rounded prices — Stripe unit_amount is integer
 * cents, and naive %-off-list math would price camper 2 ($1,049) above
 * camper 1's $999 founding price while early bird runs.
 */

export const KIDS_SERIES_SLUG = "claude-code-for-kids";
export const KIDS_MAX_CHILDREN = 4;

/* ── Early-bird reminder popup (docs/plans/2026-07-24-…-reminder-popup) ──
 * The two-step capture popup on the kids page: capture → reveal KIDS10.
 * These constants are the ONE source of truth for the code string, the
 * deadline, and the scheduled-send time, read by the popup (client), the
 * capture endpoint, and the reminder email (server). KIDS10 is a kids-scoped
 * coupon (couponCodes.series_slug), minted via scripts/kids-create-coupon.ts;
 * it stacks on the early-bird sibling ladder by design. */
export const KIDS_POPUP_CODE = "KIDS10";
export const KIDS_POPUP_PERCENT = 10;
/** Early-bird pricing ends here — $999 founding through this instant, then $1,399. */
export const KIDS_EARLY_BIRD_DEADLINE = "2026-08-11T23:59:00-04:00";
/** Human display of the deadline for popup + email copy. */
export const KIDS_EARLY_BIRD_DEADLINE_LABEL = "Tuesday, August 11";
/** When the single "before early bird closes" reminder actually sends
 *  (2 PM ET, two days ahead of the deadline). Within Resend's 30-day
 *  scheduling horizon from launch. */
export const KIDS_REMINDER_SEND_AT = "2026-08-09T14:00:00-04:00";

/** Marketing percentages by 1-indexed camper position (0 = full price). */
export const KIDS_SIBLING_PERCENTS = [0, 35, 50, 65] as const;

/** Flat per-camper ladders keyed by the camper-1 price in force. */
const LADDERS: Record<number, readonly number[]> = {
  99900: [99900, 64900, 49900, 34900], // founding / early-bird (ended 2026-08-11)
  119900: [119900, 77900, 59900, 41900], // list — lowered from $1,399 on 2026-08-12
  139900: [139900, 90900, 69900, 48900], // former list (through 2026-08-11)
};

/**
 * Per-camper price (cents) for the 1-indexed position, given camper 1's
 * price in force (early-bird-aware). Unknown bases (future admin repricing)
 * fall back to the marketing percentages rounded to a whole dollar.
 */
export function kidsChildPriceCents(basePriceCents: number, position: number): number {
  const idx = Math.min(Math.max(position, 1), KIDS_MAX_CHILDREN) - 1;
  const ladder = LADDERS[basePriceCents];
  if (ladder) return ladder[idx];
  const pct = KIDS_SIBLING_PERCENTS[idx];
  return Math.round((basePriceCents * (100 - pct)) / 100 / 100) * 100;
}

/** Family total (cents) for N campers at the given camper-1 price. */
export function kidsFamilyTotalCents(basePriceCents: number, childCount: number): number {
  let total = 0;
  const n = Math.min(Math.max(childCount, 1), KIDS_MAX_CHILDREN);
  for (let pos = 1; pos <= n; pos++) total += kidsChildPriceCents(basePriceCents, pos);
  return total;
}

export type KidsChild = { firstName: string; lastName: string; age: number };

/** Shared name cleaner for parent-supplied camper names. Strips markup-ish
 *  chars and control chars (whose \u00XX JSON escapes can push
 *  metadata.children past Stripe's 500-char metadata value limit), collapses
 *  whitespace, and slices by code point so a trailing emoji isn't split into
 *  a lone surrogate. */
function cleanName(raw: unknown): string {
  return cleanKidsName(raw, 30);
}

/** Exported variant of the checkout name cleaner with a caller-chosen length
 *  cap (code points). Used by the Demo Night RSVP to sanitize the
 *  parent-confirmed diploma name (a full name needs more than 30). */
export function cleanKidsName(raw: unknown, maxLen = 30): string {
  if (typeof raw !== "string") return "";
  return Array.from(
    raw
      .replace(/[<>\\{}"]/g, "")
      .replace(/[\u0000-\u001f\u007f]/g, "")
      .replace(/\s+/g, " ")
      .trim(),
  )
    .slice(0, maxLen)
    .join("");
}

/**
 * Sanitize + validate the parent-supplied camper rows from the checkout
 * POST. COPPA clean-hands: name + age ONLY, supplied by the parent.
 * Returns null when anything is off (caller 400s with a friendly message).
 * Age bounds (10–16) match the marketed range exactly; a family a year
 * off the band is handled by email (see the checkout note), not by
 * a just-turned-16.
 */
export function sanitizeKidsChildren(raw: unknown, expectedCount: number): KidsChild[] | null {
  if (!Array.isArray(raw)) return null;
  if (raw.length !== expectedCount || raw.length < 1 || raw.length > KIDS_MAX_CHILDREN) return null;
  const out: KidsChild[] = [];
  for (const r of raw) {
    const firstName = cleanName((r as any)?.firstName);
    const lastName = cleanName((r as any)?.lastName);
    const age = Number((r as any)?.age);
    if (!firstName || !lastName || !Number.isInteger(age) || age < 10 || age > 16) return null;
    out.push({ firstName, lastName, age });
  }
  return out;
}

/** Terms & Waiver version accepted at checkout — stamped into Stripe
 *  metadata (termsVersion) alongside the acceptance timestamp + IP so every
 *  order records exactly which document the parent agreed to. Bump when the
 *  terms change materially (keep program-terms-content.tsx's display date in
 *  sync). */
export const KIDS_TERMS_VERSION = "2026-07-23";

/** Cohort schedule facts used in checkout copy (page content owns prose).
 *  Six dated touchpoints, one per checkout schedule box. Times are ET. */
export const KIDS_COHORT_SCHEDULE = {
  /** Collapsed checkout header — the cohort identity in one line. */
  cohortLine: "August 25 \u2013 September 2 Cohort",
  subLine: "4 evening sessions + Family Demo Night",
  /** Compact variant for the sticky reserve bar's second line \u2014 must fit
   *  a 390px viewport next to the CTA without truncating. */
  barLine: "Aug 25 \u2013 Sep 2 \u00b7 6 sessions",
  items: [
    { label: "Orientation", day: "Mon, Aug 24", time: "6:30 PM ET", note: "Parents" },
    { label: "Session 1", day: "Tue, Aug 25", time: "6:30\u20138:00 PM ET" },
    { label: "Session 2", day: "Wed, Aug 26", time: "6:30\u20138:00 PM ET" },
    { label: "Session 3", day: "Thu, Aug 27", time: "6:30\u20138:00 PM ET" },
    { label: "Session 4", day: "Mon, Aug 31", time: "6:30\u20138:00 PM ET" },
    { label: "Demo Night", day: "Wed, Sep 2", time: "6:30 PM ET", note: "Families" },
  ],
} as const;

/** Flips to true when the membership dashboard (PR #92) is merged and
 *  deployed \u2014 until then prod has no /claim or /dashboard runtime code, so
 *  every account-creation CTA (thank-you page, confirmation email) must stay
 *  hidden to avoid dead links. One-line follow-up flip. */

export interface KidsSession {
  key: string;
  label: string;
  title: string;
  audience: string;
  dayEt: string;
  timeEt: string;
  startUtcIso: string;
  endUtcIso: string;
}

/**
 * The August 2026 cohort's six live touchpoints \u2014 DISPLAY DATA ONLY.
 * This module ships in the public client bundle, so it must never carry
 * Zoom join links, passcodes, or calendar event ids: those gate paid access
 * to a kids program and live server-side only in
 * server/lib/kids-sessions.ts (same key order). Zoom meetings + calendar
 * events were created 2026-07-23 by scripts/kids-schedule-sessions.ts
 * (waiting room on, cloud auto-recording, all times ET). The Zoom app is
 * create-only (no update scope): if a session must move, delete the meeting
 * + calendar event by hand, re-run the script, and update BOTH registries.
 */
export const KIDS_SESSIONS: readonly KidsSession[] = [
  {
    key: "orientation",
    label: "Orientation",
    title: "Parent Orientation + Open Q&A",
    audience: "Parents only (bring the computer your kid will use)",
    dayEt: "Mon, Aug 24",
    timeEt: "6:30\u20137:30 PM ET",
    startUtcIso: "2026-08-24T22:30:00Z",
    endUtcIso: "2026-08-24T23:30:00Z",
  },
  {
    key: "s1",
    label: "Session 1",
    title: "How AI Actually Thinks",
    audience: "Campers, with a parent nearby",
    dayEt: "Tue, Aug 25",
    timeEt: "6:30\u20138:00 PM ET",
    startUtcIso: "2026-08-25T22:30:00Z",
    endUtcIso: "2026-08-26T00:00:00Z",
  },
  {
    key: "s2",
    label: "Session 2",
    title: "Build Night",
    audience: "Campers, with a parent nearby",
    dayEt: "Wed, Aug 26",
    timeEt: "6:30\u20138:00 PM ET",
    startUtcIso: "2026-08-26T22:30:00Z",
    endUtcIso: "2026-08-27T00:00:00Z",
  },
  {
    key: "s3",
    label: "Session 3",
    title: "Make It Great",
    audience: "Campers, with a parent nearby",
    dayEt: "Thu, Aug 27",
    timeEt: "6:30\u20138:00 PM ET",
    startUtcIso: "2026-08-27T22:30:00Z",
    endUtcIso: "2026-08-28T00:00:00Z",
  },
  {
    key: "s4",
    label: "Session 4",
    title: "Use It Wisely",
    audience: "Campers, with a parent nearby",
    dayEt: "Mon, Aug 31",
    timeEt: "6:30\u20138:00 PM ET",
    startUtcIso: "2026-08-31T22:30:00Z",
    endUtcIso: "2026-09-01T00:00:00Z",
  },
  {
    key: "demo",
    label: "Demo Night",
    title: "Family Demo Night",
    audience: "Campers + the whole family",
    dayEt: "Wed, Sep 2",
    timeEt: "6:30\u20138:00 PM ET",
    startUtcIso: "2026-09-02T22:30:00Z",
    endUtcIso: "2026-09-03T00:00:00Z",
  },
] as const;

// Launch switch moved to its own module (gates ALL account entry points).
export { MEMBERS_PORTAL_LIVE } from "./members-launch";
